import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, iif, mergeMap, Observable, of, skip, Subscription, take, throwError } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PayloadInterface } from '../../../models/interface/payload.interface';
import { RequestBodyData } from '../../../models/request-body.data';
import { HelperService } from '../../../services/helper/helper.service';
import { UserInterface } from '../../../models/interface/user.interface';
import { StudyStore } from '../../../store/study/component-store/study.store';
import { HttpResponse } from '@angular/common/http';

type RoleSlug = 'study.ecoachmanager' | 'study.ecoach' | 'study.access' | 'study.publisher' | 'study.owner';

type Role = {
  id: number;
  role: RoleSlug;
};

export type StudyCollaboratorAddDataParam = { intervention_name: string };

export type StudyCollaboratorAddData = {
  param: StudyCollaboratorAddDataParam;
  studyId: number;
  collaborators: UserInterface[];
  eCoaches: UserInterface[];
  myCollaborators?: UserInterface[];
  isAdmin: boolean;
};

@Component({
  selector: 'app-dialog-intervention-collaborator-add',
  templateUrl: './dialog-intervention-collaborator-add.component.html',
  styleUrls: ['./dialog-intervention-collaborator-add.component.scss'],
  providers: [StudyStore],
})
export class DialogInterventionCollaboratorAddComponent implements OnInit, OnDestroy {
  public param: StudyCollaboratorAddDataParam;
  public studyId: number;
  public collaborators: UserInterface[];

  public eCoaches: UserInterface[];

  // Filter
  public filter = { userSelection: '' };

  isAdmin: boolean = false;

  public myCollaborators: UserInterface[];
  public myCollaboratorsSubject = new BehaviorSubject<UserInterface[]>(null);
  public myCollaborators$ = this.myCollaboratorsSubject.asObservable();
  selectedCollaborator: UserInterface;

  public eCoachesSubject = new BehaviorSubject<UserInterface[]>([]);
  public eCoaches$ = this.eCoachesSubject.asObservable();

  // List of roles
  public roles = ['study.ecoachmanager', 'study.ecoach', 'study.access'] as const;
  public selectedRole: typeof this.roles[number];

  // Selected collaborator
  public selectedECoach: UserInterface;

  // Form submission
  submitted = false;

  public addCollaboratorResponse = new BehaviorSubject<'DEFAULT' | 'LOADING' | 'SUCCESS' | 'FAILURE'>('DEFAULT');

  private addCollaboratorsResponse$: Observable<any>;

  // Subscription Handler
  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: StudyCollaboratorAddData,
    private helperService: HelperService,
    private dialogRef: MatDialogRef<DialogInterventionCollaboratorAddComponent>,
    private studyStore: StudyStore,
  ) {
    this.addCollaboratorsResponse$ = this.studyStore.addCollaboratorsResponse$;
  }

  // Helper function
  public get helper() {
    return this.helperService;
  }

  ngOnInit(): void {
    this.collaborators = this.data.collaborators;
    this.param = this.data.param;
    this.studyId = this.data.studyId;
    this.isAdmin = this.data.isAdmin;
    this.eCoaches = this.data.eCoaches;
    this.myCollaborators = this.data.myCollaborators || null;
    if (this.myCollaborators) {
      this.myCollaborators = this.myCollaborators.filter(
        (myCollaborator) =>
          this.collaborators.every((collaborator) => myCollaborator.id !== collaborator.id),
      );
      this.myCollaboratorsSubject.next(this.myCollaborators);
      if (this.myCollaborators.length > 0) {
        this.selectedCollaborator = this.myCollaborators[0];
      }
    }
    this.eCoachesSubject.next(this.eCoaches);
    if (this.eCoaches.length > 0) {
      this.selectedECoach = this.eCoaches[0];
    }
  }

  public isNoneSelectedAddCollaboratorForm(): boolean {
    if (this.isAdmin) {
      return !this.selectedECoach || !this.selectedRole;
    } else {
      return !this.selectedCollaborator || !this.selectedRole;
    }
  }

  // Adding collaborator to study
  public addCollaborator(): void {
    if (this.isNoneSelectedAddCollaboratorForm() || this.addCollaboratorResponse.value !== 'DEFAULT') {
      return;
    }
    const collaboratorId = this.isAdmin ? this.selectedECoach.id : this.selectedCollaborator.id;
    const addRoles: Role[] = [];
    addRoles.push({ id: collaboratorId, role: this.selectedRole });
    const payload: PayloadInterface = new RequestBodyData('users', { users: addRoles });
    this.addCollaboratorResponse.next('LOADING');
    this.studyStore.addCollaborators({ studyId: this.studyId, payload });
    this.subscriptions.push(
      this.addCollaboratorsResponse$
        .pipe(
          skip(1),
          take(1),
          mergeMap(
            (result) => iif(
              () => result instanceof HttpResponse,
              of(result),
              throwError(() => result),
            ),
          ),
        )
        .subscribe({
          next: () => {
            this.addCollaboratorResponse.next('SUCCESS');
          },
          error: () => {
            this.addCollaboratorResponse.next('FAILURE');
            setTimeout(() => {
              this.addCollaboratorResponse.next('DEFAULT');
            }, 2500);
          },
          complete: () => {
            setTimeout(() => {
              this.addCollaboratorResponse.next('DEFAULT');
              this.dialogRef.close('SUCCESS');
            }, 2500);
          },
        }),
    );
  }

  public resetFilter(): void {
    this.filter.userSelection = '';
    if (this.isAdmin) {
      this.eCoachesSubject.next(this.eCoaches);
    } else {
      this.myCollaboratorsSubject.next(this.myCollaborators);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
